/**
 * Accepted spreadsheet columns for the student import (POST /importStudents).
 *
 * Headers are matched exactly against these lists after the API normalizes them
 * (lowercase, newlines to spaces, repeated whitespace collapsed, decorative
 * qualifiers stripped). Keep this in sync with the API's synonym list.
 */
export const STUDENT_IMPORT_COLUMNS = [
    {
        field: 'First Name', requiredColumn: true,
        headers: ['first name', 'first', 'legal first name', 'fname', 'given name', 'student first', 'student first name']
    },
    {
        field: 'Last Name', requiredColumn: true,
        headers: ['last name', 'last', 'legal last name', 'lname', 'surname', 'student last', 'student last name']
    },
    {
        field: 'Grade', requiredColumn: true,
        headers: ['grade', 'grade level', 'gr', 'gl', 'current grade', 'grade/year', 'student grade']
    },
    {
        field: 'Student ID', requiredColumn: true,
        headers: ['student id', 'student id from sis', 'studentid', 'student number', 'student #']
    },
    {
        field: 'Teacher',
        headers: ['teacher', 'teacher name', 'homeroom teacher', 'homeroom', 'advisor', 'classroom teacher', 'classroom']
    },
    {
        field: 'Parent Email', requiredColumn: true, blankAllowed: true,
        headers: ['parent email', 'parent email id', 'guardian email', 'parent/guardian email', 'primary email', 'email', 'contact email']
    },
    {
        field: 'Alt Email',
        headers: ['parent alternate email', 'secondary email', 'alt email', 'guardian 2 email', 'other email']
    },
    {
        field: 'Phone',
        headers: ['parent mobile number', 'cell', 'mobile', 'phone', 'parent phone', 'guardian phone', 'contact number', 'phone number']
    },
    {
        field: 'Address 1',
        headers: ['number/street/apt', 'address', 'street address', 'address line 1', 'mailing address']
    },
    {
        field: 'City/State/Zip',
        headers: ['city/state/zip', 'city state zip', 'address line 2', 'csz']
    },
    {
        field: 'Reduced',
        headers: ['reduced eligibility', 'reduced', 'reduced lunch', 'reduced-price meals', 'reduced price']
    },
    {
        field: 'Free',
        headers: ['free eligibility', 'free', 'free meals', 'free lunch']
    },
    {
        field: 'Before Care',
        headers: ['before care', 'am care', 'before school care']
    },
    {
        field: 'Milk Card',
        headers: ['milk card', 'milk', 'has milk card']
    },
    {
        field: 'B&AC Packet',
        headers: ['b&ac', 'before & after care packet', 'bac packet', 'enroll b&ac packet']
    },
    {
        field: 'PIN',
        headers: ['pin', 'lunch pin', 'meal pin', 'pos pin', 'student pin']
    }
];

/** Qualifiers ignored anywhere in a header, so "Before Care (Yes/No/Blank)" matches "before care". */
export const IGNORED_QUALIFIERS = [
    '(optional)', '[optional]',
    '(y/n/blank)', '[y/n/blank]',
    '(yes/no/blank)', '[yes/no/blank]',
    '(from sis)', '[from sis]'
];

/** The header row may appear anywhere in the first this-many rows of the sheet. */
export const HEADER_ROW_SEARCH_DEPTH = 6;

/**
 * Grade spellings the import tolerates before validating against the school's
 * grades. Deliberately phrased as spelling tolerance only: it does not claim
 * which configured grade a given spelling resolves to, because a school can
 * configure Preschool and PK as separate grades. The authoritative answer is
 * the school's own configured grade list, which is shown on the upload screen.
 */
export const GRADE_FORMAT_NOTES = [
    'Capitalization and extra spaces do not matter.',
    'Kindergarten may be written as K, kg, kinder or Kindergarten.',
    'Pre-K may be written as Pre-K, prek or PK, and Preschool as preschool - a trailing level is kept (Pre-K 2, PK3, Preschool #4).',
    'Grades may be ordinals (third, 11th, 3rd Grade, Grade 3) or plain numbers (1 - 12, and padded forms such as 03).',
    'Whichever spelling you use, the grade must resolve to one of the grades listed above for this school - anything else is skipped.'
];

/** How individual cell values are cleaned up before they are stored. */
export const VALUE_NOTES = [
    'Values are trimmed, and "nan" or "none" are treated as empty.',
    'Student ID and PIN drop a trailing ".0" (444444.0 becomes 444444) and keep leading zeros (007 stays 007).',
    'A blank PIN is stored as empty.',
    'Phone numbers are reduced to digits, keeping a leading + ((336)906-2584 becomes 3369062584).',
    'CSV (.csv) files follow all of the same rules as .xlsx and .xls.'
];

/** Column names that must be present in the sheet, for the "mandatory fields are missing" case. */
export const REQUIRED_COLUMN_FIELDS = STUDENT_IMPORT_COLUMNS
    .filter(col => col.requiredColumn)
    .map(col => col.field);



// WEBPACK FOOTER //
// ./src/components/SchoolAdmin/SetUpSchool/studentImportColumns.js